/**
 * <media-carousel> — carrousel horizontal générique (rangée de cartes défilable).
 *
 * Composant UNIQUE partagé par « Vous aimerez aussi » (sections/featured-products)
 * et « Accords parfaits » (snippets/accords-parfaits) — remplace les anciens
 * <product-carousel> et <accords-carousel>, dont la logique était identique
 * (dette technique supprimée).
 *
 * Progressive enhancement : sans JavaScript, la rangée défile déjà au swipe
 * (scroll-snap CSS sur [data-carousel-track]) et les flèches restent masquées
 * (attribut `hidden`). Le JS ne fait que RÉVÉLER les flèches et les câbler pour
 * faire défiler la piste ; leur affichage réel (desktop) est piloté par le CSS.
 * Aucun appel réseau. Respecte prefers-reduced-motion.
 *
 * Attributs attendus sur les enfants :
 *   [data-carousel-track] — la piste défilable (obligatoire)
 *   [data-carousel-prev]  — bouton précédent (obligatoire)
 *   [data-carousel-next]  — bouton suivant (obligatoire)
 * Attributs optionnels sur l'élément :
 *   data-carousel-min-step — pas de défilement minimal en px (défaut 200)
 *   data-carousel-loop     — boucle : au bout, « suivant » revient au début (et
 *                            inversement) ; les flèches ne se désactivent jamais.
 *                            OPT-IN : sans cet attribut, comportement fini d'origine
 *                            (les autres carrousels du thème ne changent pas).
 *   data-carousel-count    — indicateur de position. OPT-IN également : sans lui,
 *                            aucun des carrousels existants ne change.
 *   data-carousel-hide-idle — masque ENTIÈREMENT les flèches quand il n'y a rien à
 *                            défiler, au lieu de les laisser grisées. OPT-IN.
 *                            Utilisé par la barre de rayons : en desktop les 6 puces
 *                            tiennent (1 400 px de contenu pour 1 400 px utiles),
 *                            deux flèches mortes n'y auraient aucun sens.
 *   data-carousel-reveal="<sélecteur>" — amène cet élément dans la zone visible AU
 *                            CHARGEMENT. OPT-IN. Utilisé par la barre de rayons pour
 *                            la puce du rayon courant : sans lui, sur un rayon dont la
 *                            puce est dans la zone cachée, le visiteur n'avait AUCUN
 *                            repère « vous êtes ici ».
 *
 * Indicateur (data-carousel-count) : il compte des ÉLÉMENTS, pas des « pages ».
 * Un modèle « page X sur Y » ne tombe pas juste ici — la dernière vignette dépasse
 * volontairement du bord (2,5 visibles), donc la dernière page est toujours
 * partielle et l'indicateur affichait « 2/3 » une fois arrivé au bout. Le compte
 * d'éléments visibles, lui, est exact aux deux extrémités : « 1–3 », puis « 5–7 ».
 * Enfants optionnels : [data-carousel-range] (texte) et [data-carousel-dots] (points).
 */
class MediaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track || !this.prev || !this.next) return;

    this.minStep = parseInt(this.getAttribute('data-carousel-min-step'), 10) || 200;
    this.loop = this.hasAttribute('data-carousel-loop');
    this.hideIdle = this.hasAttribute('data-carousel-hide-idle');

    // Indicateur de position — opt-in, silencieux si les éléments sont absents.
    this.range = this.hasAttribute('data-carousel-count') ? this.querySelector('[data-carousel-range]') : null;
    this.dots = this.hasAttribute('data-carousel-count') ? this.querySelector('[data-carousel-dots]') : null;
    if (this.dots) {
      this.dots.innerHTML = Array.from(this.track.children, () => '<span class="carousel-dot"></span>').join('');
    }

    // Flèches masquées sans JS → révélées ici. Le CSS décide de les afficher
    // (desktop) ou non (mobile = swipe).
    this.prev.hidden = false;
    this.next.hidden = false;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onPrev = () => this.scrollByStep(-1);
    this.onNext = () => this.scrollByStep(1);
    this.onScroll = () => this.updateArrows();

    this.prev.addEventListener('click', this.onPrev);
    this.next.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);

    const revealSelector = this.getAttribute('data-carousel-reveal');
    if (revealSelector) this.reveal(revealSelector);

    this.updateArrows();
  }

  /**
   * OPT-IN (data-carousel-reveal) — amène l'élément désigné dans la zone visible de la
   * piste, avec une marge qui laisse volontairement voir l'élément précédent COUPÉ :
   * c'est cet élément coupé qui signale qu'on peut revenir en arrière.
   *
   * ⛔ Volontairement PAS scrollIntoView() : sur une piste imbriquée dans la page, il
   * déplace aussi le défilement VERTICAL de la fenêtre — le visiteur arriverait sur la
   * page déjà défilée. On calcule donc scrollLeft à la main, ce qui ne touche que la piste.
   */
  reveal(selector) {
    const target = this.track.querySelector(selector);
    if (!target) return;
    const piste = this.track.getBoundingClientRect();
    const cible = target.getBoundingClientRect();
    const marge = 20;
    if (cible.left < piste.left) {
      this.track.scrollLeft -= piste.left - cible.left + marge;
    } else if (cible.right > piste.right) {
      this.track.scrollLeft += cible.right - piste.right + marge;
    }
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  scrollByStep(direction) {
    const behavior = this.reduceMotion ? 'auto' : 'smooth';
    // Mode boucle : au bout, on repart de l'autre côté au lieu de rester bloqué.
    if (this.loop) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (direction > 0 && x >= max - 2) {
        this.track.scrollTo({ left: 0, behavior });
        return;
      }
      if (direction < 0 && x <= 2) {
        this.track.scrollTo({ left: max, behavior });
        return;
      }
    }
    // ~80 % de la largeur visible → défilement par « page » en gardant un repère.
    const amount = Math.max(this.track.clientWidth * 0.8, this.minStep) * direction;
    this.track.scrollBy({ left: amount, behavior });
  }

  updateArrows() {
    this.updateCount();
    // OPT-IN : rien à défiler → on masque les flèches au lieu de les griser.
    // Tolérance de 2 px, la même que pour les extrémités (arrondis de sous-pixel).
    if (this.hideIdle) {
      const rienADefiler = this.track.scrollWidth - this.track.clientWidth <= 2;
      this.prev.hidden = rienADefiler;
      this.next.hidden = rienADefiler;
      if (rienADefiler) return;
    }
    // En mode boucle, les flèches restent toujours actives (pas de cul-de-sac).
    if (this.loop) {
      this.prev.disabled = false;
      this.next.disabled = false;
      return;
    }
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    // Tolérance de 2 px pour les arrondis de sous-pixel.
    this.prev.disabled = x <= 2;
    this.next.disabled = x >= max - 2;
  }

  /**
   * Met à jour l'indicateur « premier–dernier » des éléments actuellement à l'écran.
   * Le pas est mesuré sur le PREMIER élément (largeur + écart réel) plutôt que codé
   * en dur : la largeur des vignettes est calculée en CSS et change avec le viewport.
   */
  updateCount() {
    if (!this.range && !this.dots) return;
    const items = this.track.children;
    const total = items.length;
    if (!total) return;

    const first = items[0].getBoundingClientRect().width;
    const step = total > 1
      ? items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
      : first;
    if (step <= 0) return;

    // Même tolérance de 2 px que pour les flèches : les positions de défilement sont
    // fractionnaires (219,5 px au lieu de 220), et une division stricte fait tomber le
    // premier index une vignette trop tôt une fois arrivé au bout (« 2–5 » au lieu de
    // « 3–5 sur 5 », mesuré en desktop avant correction).
    const x = this.track.scrollLeft;
    const to = Math.min(total, Math.ceil((x + this.track.clientWidth) / step));
    const from = Math.min(to, Math.floor((x + 2) / step) + 1);

    if (this.range) this.range.textContent = from === to ? String(from) : `${from}–${to}`;
    if (this.dots) {
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.classList.toggle('is-visible', i >= from - 1 && i <= to - 1);
      });
    }
  }
}

if (!customElements.get('media-carousel')) {
  customElements.define('media-carousel', MediaCarousel);
}
